import type { Question, Source } from '../types'

interface Props { question: Question; source?: Source; showImage?: boolean; shorterSource?: boolean; enlargedImage?: boolean }
interface TableData { headers?: string[]; rows?: string[][] }
interface ChartData { labels?: string[]; values?: number[]; unit?: string }

function shorten(text: string, limit = 280) {
  if (text.length <= limit) return text
  const sentenceEnd = text.slice(0, limit).lastIndexOf('.')
  return `${text.slice(0, sentenceEnd > 120 ? sentenceEnd + 1 : limit).trim()}…`
}

export function Stimulus({ question, source, showImage = true, shorterSource = false, enlargedImage = false }: Props) {
  const table = question.stimulusData as TableData | null
  const chart = question.stimulusData as ChartData | null
  const hasStimulus = Boolean(question.stimulus || (showImage && question.imagePath) || question.stimulusType === 'table' || question.stimulusType === 'chart')
  if (!hasStimulus && !source) return null
  const stimulusText = shorterSource ? shorten(question.stimulus, 220) : question.stimulus

  return (
    <section className={`stimulus-card v4-stimulus ${question.imagePath ? 'visual-stimulus' : ''} ${enlargedImage ? 'enlarged-stimulus' : ''}`} aria-label="Fuente o recurso para responder">
      <div className="stimulus-layout">
        {showImage && question.imagePath && (
          <figure className="image-evidence">
            <img className="stimulus-image" src={question.imagePath} alt={`Fuente visual para analizar: ${question.topic}`} />
            <figcaption>
              <a className="open-image-link" href={question.imagePath} target="_blank" rel="noopener noreferrer">
                Abrir imagen en una pestaña nueva <span aria-hidden="true">↗</span>
              </a>
            </figcaption>
          </figure>
        )}
        <div className="stimulus-content">
          {stimulusText && (
            question.stimulus.length > 420 && !shorterSource
              ? <details className="source-fragment"><summary>Leer fragmento histórico</summary><blockquote>{question.stimulus}</blockquote></details>
              : <blockquote>{stimulusText}</blockquote>
          )}
          {question.stimulusType === 'table' && table?.headers && (
            <div className="table-wrapper compact-table"><table><thead><tr>{table.headers.map((item) => <th key={item}>{item}</th>)}</tr></thead><tbody>{table.rows?.map((row, index) => <tr key={index}>{row.map((cell, cellIndex) => <td key={`${index}-${cellIndex}`}>{cell}</td>)}</tr>)}</tbody></table></div>
          )}
          {question.stimulusType === 'chart' && chart?.values && (
            <div className="mini-chart" aria-label="Gráfico de datos">
              {chart.values.map((value, index) => {
                const max = Math.max(...chart.values!, 1)
                return <div className="chart-column" key={`${chart.labels?.[index]}-${value}`}><span className="chart-value">{value}{chart.unit ?? ''}</span><span className="chart-bar" style={{ height: `${Math.max(12, (value / max) * 110)}px` }} /><span className="chart-label">{chart.labels?.[index]}</span></div>
              })}
            </div>
          )}
        </div>
      </div>
      {source && (
        <details className="source-reference v4-source-reference">
          <summary>Fuente</summary>
          <div className="source-popover">
            <p><strong>{source.title || 'Referencia histórica'}</strong></p>
            <p>{source.author || source.origin} · {source.date || 's. f.'}</p>
            {source.origin && source.origin !== source.author && <p>{source.origin}</p>}
            {source.adaptation && <p>{source.adaptation}</p>}
            {source.url && <a href={source.url} target="_blank" rel="noopener noreferrer">Abrir referencia en una pestaña nueva ↗</a>}
          </div>
        </details>
      )}
    </section>
  )
}
